import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import IndiaMap from "../components/IndiaMap";
import { api } from "../lib/api";
import { logError } from "../lib/log";
import {
  Building2, BedDouble, MapPin, Activity, IndianRupee, ArrowRight, TrendingUp,
  TrendingDown, Briefcase, Hourglass, CheckCircle2, XCircle, FileSpreadsheet,
  Sparkles, Percent, Lock,
} from "lucide-react";

const fmtCr = (v) => v == null ? "—" : `₹${(+v).toFixed(2)} Cr`;
const fmtL = (v) => v == null ? "—" : `₹${(+v).toLocaleString("en-IN", { maximumFractionDigits: 1 })} L`;
const fmtPct = (v) => v == null ? "—" : `${(+v).toFixed(1)}%`;
const fmtNum = (v) => v == null ? "—" : (+v).toLocaleString("en-IN");

/**
 * Single, consistent KPI tile. Same height, same internal structure, single-line
 * sub-label so nothing reflows ugly when the dashboard is responsive.
 */
function Kpi({ icon: Icon, label, value, sub, accent = "bg-zinc-100", iconColor = "text-zinc-700", tone, dark = false, testid, onClick }) {
  return (
    <div
      onClick={onClick}
      data-testid={testid}
      className={`relative h-[112px] rounded-xl p-4 flex flex-col justify-between transition-all overflow-hidden ${dark ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200/80 hover:border-zinc-300"} ${onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${accent}`}>
          <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
        </div>
        <span className={`font-mono-tech text-[9px] uppercase tracking-[0.18em] truncate ${dark ? "text-zinc-400" : "text-zinc-400"}`}>{label}</span>
      </div>
      <div>
        <div className={`font-display font-black text-[24px] tracking-tight leading-none ${tone || (dark ? "text-white" : "text-zinc-900")}`} data-testid={`${testid}-value`}>
          {value}
        </div>
        {sub && <div className={`text-[10.5px] mt-1.5 truncate ${dark ? "text-zinc-400" : "text-zinc-500"}`}>{sub}</div>}
      </div>
    </div>
  );
}

const STAGE_COLOR = {
  "Financial Review": "#8FCAE7",
  "Under Discussion": "#FFCC00",
  "Approved": "#20B2AA",
  "Rejected": "#FF69B4",
};

const STAGE_BADGE = (s) => ({
  "Financial Review": "bg-sky-100 text-sky-800 border-sky-200",
  "Under Discussion": "bg-yellow-100 text-yellow-800 border-yellow-300",
  "Approved": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Rejected": "bg-rose-100 text-rose-700 border-rose-200",
})[s] || "bg-zinc-100 text-zinc-700 border-zinc-200";

function SectionHeader({ kicker, kickerColor = "text-brand-yellowDark", title, right }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-3">
      <div className="min-w-0">
        <div className={`font-mono-tech text-[10px] uppercase tracking-[0.28em] ${kickerColor}`}>{kicker}</div>
        <h2 className="font-display font-black text-[22px] tracking-tight text-zinc-900 mt-0.5">{title}</h2>
      </div>
      {right}
    </div>
  );
}

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [properties, setProperties] = useState([]);
  const [icSummary, setIcSummary] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [k, p, ic] = await Promise.all([
          api.get("/dashboard/kpis"),
          api.get("/mis/properties"),
          api.get("/dashboard/property-ic-summary"),
        ]);
        setKpis(k.data); setProperties(p.data); setIcSummary(ic.data);
      } catch (e) { logError("dashboard load failed", e); }
    })();
  }, []);

  const occTier = (pct) => pct >= 70 ? "text-emerald-600" : pct >= 55 ? "text-amber-600" : "text-rose-500";
  const topPerf = [...properties].sort((a, b) => b.occupancy_pct - a.occupancy_pct).slice(0, 5);
  const watchList = [...properties].sort((a, b) => a.occupancy_pct - b.occupancy_pct).slice(0, 5);

  const ebitdaPositive = (kpis?.monthly_ebitda_lakhs ?? 0) >= 0;

  return (
    <AppShell title="Dashboard" subtitle={`${kpis?.operational_properties ?? "—"} operational · ${kpis?.latest_month || "Live"} · ${icSummary?.total_count ?? 0} ICs in flight`}>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6" data-testid="dashboard-split">

        {/* ============= LEFT — OPERATIONAL · MIS LIVE ============= */}
        <section className="space-y-4" data-testid="ops-section">
          <SectionHeader
            kicker="OPERATIONAL · MIS LIVE"
            title="Network performance"
            right={<span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500 whitespace-nowrap">As of {kpis?.latest_month || "—"}</span>}
          />

          {/* Capacity row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi testid="kpi-active" icon={Building2} accent="bg-brand-yellow"          label="LIVE STOPS"   value={kpis?.operational_properties ?? "—"} sub="operational" />
            <Kpi testid="kpi-beds"   icon={BedDouble}  accent="bg-brand-pink/30"        label="AVAILABLE BEDS" value={fmtNum(kpis?.total_available_beds ?? kpis?.total_capacity_beds)}   sub={`${fmtNum(kpis?.total_beds_sold)} sold · ${fmtNum(kpis?.total_capacity_beds)} rated capacity`} />
            <Kpi testid="kpi-occupancy" icon={Activity}  accent="bg-brand-teal/30"      label="AVG OCCUPANCY" value={fmtPct(kpis?.avg_occupancy_pct)} sub="sold ÷ available" tone={occTier(kpis?.avg_occupancy_pct)} />
            <Kpi testid="kpi-states" icon={MapPin}     accent="bg-brand-orange/30"      label="STATES"       value={kpis?.states_count ?? "—"}             sub={`${kpis?.business_count ?? 0} biz · ${(kpis?.leisure_north_count ?? 0) + (kpis?.leisure_south_count ?? 0)} leisure`} />
          </div>

          {/* Money + Rate row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi testid="kpi-monthly-rev"    icon={IndianRupee} accent="bg-zinc-100" iconColor="text-zinc-700" label="MONTHLY REVENUE" value={fmtL(kpis?.monthly_revenue_lakhs)} sub={`${fmtCr(kpis?.annual_revenue_cr)} annualised`} />
            <Kpi testid="kpi-monthly-ebitda" icon={ebitdaPositive ? TrendingUp : TrendingDown} dark accent={ebitdaPositive ? "bg-emerald-500/20" : "bg-rose-500/20"} iconColor={ebitdaPositive ? "text-emerald-300" : "text-rose-300"} label="MONTHLY EBITDA" value={<span className={ebitdaPositive ? "text-emerald-300" : "text-rose-300"}>{fmtL(kpis?.monthly_ebitda_lakhs)}</span>} sub={`Margin ${fmtPct(kpis?.ebitda_margin_pct)} · ${fmtCr(kpis?.annual_ebitda_cr)} / yr`} />
            <Kpi testid="kpi-adr"            icon={Briefcase}   accent="bg-purple-100" iconColor="text-purple-700" label="AVG ADR"    value={`₹${fmtNum(kpis?.avg_adr)}`}    sub="per occupied night" />
            <Kpi testid="kpi-revpab"         icon={Percent}     accent="bg-sky-100"    iconColor="text-sky-700"    label="AVG REVPAB" value={`₹${kpis?.avg_revpab ?? "—"}`}  sub="₹ per available bed/day" />
          </div>

          {/* Property mix — compact pill row */}
          <div className="bg-white border border-zinc-200/80 rounded-xl p-3 flex items-center gap-2 flex-wrap" data-testid="ops-type-mix">
            <span className="font-mono-tech text-[9.5px] uppercase tracking-[0.2em] text-zinc-500 mr-2">Property mix</span>
            <span className="px-2.5 py-1 bg-brand-yellow rounded-full text-[11px] font-medium">Business · {kpis?.business_count ?? 0}</span>
            <span className="px-2.5 py-1 bg-brand-pink/30 rounded-full text-[11px] font-medium">Leisure-N · {kpis?.leisure_north_count ?? 0}</span>
            <span className="px-2.5 py-1 bg-brand-teal/30 rounded-full text-[11px] font-medium">Leisure-S · {kpis?.leisure_south_count ?? 0}</span>
            <span className="px-2.5 py-1 bg-zinc-900 text-white rounded-full text-[11px] font-medium">COCO · {kpis?.coco_count ?? 0}</span>
            <span className="px-2.5 py-1 bg-zinc-700 text-white rounded-full text-[11px] font-medium">FOCO · {kpis?.foco_count ?? 0}</span>
          </div>

          {/* Map */}
          <div className="bg-white border border-zinc-200/80 rounded-xl overflow-hidden" data-testid="map-card">
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
              <div>
                <div className="font-mono-tech text-[9.5px] uppercase tracking-[0.2em] text-zinc-500">Footprint</div>
                <h3 className="font-display font-bold text-[13px] tracking-tight">India map</h3>
              </div>
              <span className="font-mono-tech text-[9px] uppercase tracking-[0.18em] text-zinc-400">marker = bed capacity</span>
            </div>
            <div className="p-3 h-[320px]"><IndiaMap properties={properties} /></div>
          </div>

          {/* Top + Watch */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <PerfList title="Top performers" subtitle="Highest occupancy" items={topPerf} icon={TrendingUp} iconColor="text-emerald-600" testid="top-performers" navigate={navigate} occTier={occTier} prefix="top" />
            <PerfList title="Watch list"     subtitle="Lowest occupancy"  items={watchList} icon={TrendingDown} iconColor="text-rose-500" testid="watch-list"  navigate={navigate} occTier={occTier} prefix="watch" />
          </div>
        </section>

        {/* ============= RIGHT — PROPERTY IC PIPELINE ============= */}
        <section className="space-y-4" data-testid="ic-section">
          <SectionHeader
            kicker="PROPERTY IC · INVESTMENT PIPELINE"
            kickerColor="text-brand-purple"
            title="Deal flow & approvals"
            right={
              <button
                onClick={() => navigate("/property-ic")}
                data-testid="open-ic"
                className="flex items-center gap-1.5 font-mono-tech text-[9.5px] uppercase tracking-[0.2em] text-zinc-900 bg-brand-yellow px-3 py-1.5 rounded-full hover:bg-zinc-900 hover:text-brand-yellow transition-colors whitespace-nowrap"
              >
                Open Property IC <ArrowRight className="w-3 h-3" />
              </button>
            }
          />

          {/* Status counts — same height tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ic-status-tiles">
            <Kpi testid="ic-pipeline-count"  icon={Hourglass}        accent="bg-amber-100"   iconColor="text-amber-700"   label="IN PIPELINE"  value={icSummary?.inbox_active ?? 0}        sub="emails queued" onClick={() => navigate("/property-ic")} />
            <Kpi testid="ic-approved-count"  icon={CheckCircle2}     accent="bg-emerald-100" iconColor="text-emerald-700" label="APPROVED"     value={icSummary?.approved?.count ?? 0}     sub={`${icSummary?.approved?.beds ?? 0} beds · ${fmtCr(icSummary?.approved?.capex_cr)}`} tone="text-emerald-700" onClick={() => navigate("/property-ic")} />
            <Kpi testid="ic-rejected-count"  icon={XCircle}          accent="bg-rose-100"    iconColor="text-rose-600"    label="REJECTED"     value={icSummary?.rejected?.count ?? 0}     sub="hard no"            tone="text-rose-600" />
            <Kpi testid="ic-total-count"     icon={FileSpreadsheet}  accent="bg-zinc-200"    iconColor="text-zinc-600"    label="TOTAL ICS"    value={icSummary?.total_count ?? 0}         sub="all-time submitted" />
          </div>

          {/* Pipeline + Approved economics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Pipeline */}
            <div className="rounded-xl border-2 border-amber-300 bg-amber-50/40 p-4 space-y-2" data-testid="ic-pipeline-econ">
              <div className="flex items-center justify-between">
                <span className="font-mono-tech text-[9.5px] uppercase tracking-[0.2em] text-amber-700">Pipeline</span>
                <Hourglass className="w-3.5 h-3.5 text-amber-600" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="font-display font-black text-[26px] text-zinc-900 leading-none">{icSummary?.inbox_active ?? 0}</span>
                <span className="text-[11px] text-zinc-500 font-medium">leads</span>
              </div>
              <div className="text-[11px] text-zinc-600">{icSummary?.pipeline?.count ?? 0} IC{(icSummary?.pipeline?.count ?? 0) === 1 ? "" : "s"} in active review · {icSummary?.pipeline?.beds ?? 0} beds projected</div>
              <div className="text-[10.5px] text-amber-800 pt-1.5 border-t border-amber-200/70 leading-relaxed">
                Active IC projection: <b>{fmtCr(icSummary?.pipeline?.annual_revenue_cr)}</b> annual rev · EBITDA {fmtCr(icSummary?.pipeline?.annual_ebitda_cr)} · ROI ~{icSummary?.pipeline?.avg_roi_months ?? "—"} mo
              </div>
            </div>

            {/* Approved */}
            <div className="rounded-xl bg-zinc-900 text-white p-4 space-y-2" data-testid="ic-approved-econ">
              <div className="flex items-center justify-between">
                <span className="font-mono-tech text-[9.5px] uppercase tracking-[0.2em] text-emerald-300">Approved · Locked-in</span>
                <Lock className="w-3.5 h-3.5 text-emerald-300" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="font-display font-black text-[26px] leading-none">{fmtCr(icSummary?.approved?.annual_revenue_cr)}</span>
                <span className="text-[11px] text-zinc-400">/ yr</span>
              </div>
              <div className="text-[11px] text-zinc-400">EBITDA {fmtCr(icSummary?.approved?.annual_ebitda_cr)} ({fmtPct(icSummary?.approved?.avg_margin_pct)} margin)</div>
              <div className="text-[10.5px] text-emerald-300/90 pt-1.5 border-t border-zinc-700/80 leading-relaxed">
                {icSummary?.approved?.beds ?? 0} beds · Capex committed <b>{fmtCr(icSummary?.approved?.capex_cr)}</b> · ROI {icSummary?.approved?.avg_roi_months ?? "—"} mo
              </div>
            </div>
          </div>

          {/* Stage flow */}
          <div className="bg-white border border-zinc-200/80 rounded-xl p-4" data-testid="ic-stage-flow">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-mono-tech text-[9.5px] uppercase tracking-[0.2em] text-zinc-500">Stage flow</div>
                <h3 className="font-display font-bold text-[13px] tracking-tight">Where each IC sits</h3>
              </div>
              <span className="font-mono-tech text-[9px] uppercase tracking-[0.18em] text-zinc-400 whitespace-nowrap">{icSummary?.total_count ?? 0} total</span>
            </div>
            <div className="space-y-2">
              {(icSummary?.by_stage || []).map(s => {
                const max = Math.max(...(icSummary?.by_stage || []).map(x => x.count), 1);
                const w = (s.count / max) * 100;
                return (
                  <div key={s.stage} data-testid={`ic-stage-${s.stage.replace(/\s/g, '')}`}>
                    <div className="flex justify-between items-center text-[11.5px] mb-1">
                      <span className="font-display font-semibold inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: STAGE_COLOR[s.stage] }} />
                        {s.stage}
                      </span>
                      <span className="font-mono-tech text-zinc-500 text-[10.5px] tabular-nums">
                        {s.count} · {s.beds_est} beds{s.deal_value_lakhs ? ` · ₹${s.deal_value_lakhs}L/mo` : ""}
                      </span>
                    </div>
                    <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${w}%`, background: STAGE_COLOR[s.stage] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent IC submissions */}
          <div className="bg-white border border-zinc-200/80 rounded-xl overflow-hidden" data-testid="ic-recent">
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-brand-yellow flex-shrink-0" />
              <div>
                <div className="font-mono-tech text-[9.5px] uppercase tracking-[0.2em] text-zinc-500">Latest submissions</div>
                <h3 className="font-display font-bold text-[13px]">Recent IC activity</h3>
              </div>
            </div>
            <div className="divide-y divide-zinc-100">
              {(icSummary?.recent || []).map(p => (
                <div
                  key={p.submission_id}
                  className="px-4 py-3 hover:bg-zinc-50 cursor-pointer transition-colors"
                  data-testid={`recent-${p.submission_id}`}
                  onClick={() => navigate("/property-ic")}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-display font-bold text-[13px] truncate text-zinc-900">{p.name}</h4>
                        <span className={`text-[9.5px] uppercase tracking-wide px-1.5 py-0.5 rounded border whitespace-nowrap ${STAGE_BADGE(p.status)}`}>{p.status}</span>
                      </div>
                      <div className="text-[10.5px] text-zinc-500 mt-0.5 truncate">{p.city}, {p.state} · {p.beds} beds · {p.submitted_by_name || "—"}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-display font-bold text-[12.5px] text-zinc-900 tabular-nums">{fmtL(p.monthly_revenue_lakhs)}<span className="text-[9.5px] text-zinc-400 ml-0.5 font-normal">/mo</span></div>
                      <div className="text-[10px] text-zinc-500 tabular-nums">EBITDA {fmtL(p.monthly_ebitda_lakhs)} · ROI {p.roi_months ? `${p.roi_months}mo` : "—"}</div>
                    </div>
                  </div>
                </div>
              ))}
              {!icSummary?.recent?.length && (
                <div className="px-4 py-8 text-center font-mono-tech text-[10px] uppercase tracking-wide text-zinc-400">
                  No IC submissions yet
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function PerfList({ title, subtitle, items, icon: Icon, iconColor, testid, navigate, occTier, prefix }) {
  return (
    <div className="bg-white border border-zinc-200/80 rounded-xl overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
        <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${iconColor}`} />
        <div>
          <div className="font-mono-tech text-[9.5px] uppercase tracking-[0.2em] text-zinc-500">{title}</div>
          <h3 className="font-display font-bold text-[12.5px]">{subtitle}</h3>
        </div>
      </div>
      <div className="divide-y divide-zinc-100">
        {items.map(p => (
          <div key={p.location} onClick={() => navigate(`/operational/${encodeURIComponent(p.location)}`)} className="px-4 py-2.5 flex items-center gap-3 hover:bg-zinc-50 cursor-pointer transition-colors" data-testid={`${prefix}-${p.location}`}>
            <div className={`font-display font-black text-[15px] w-12 tabular-nums ${occTier(p.occupancy_pct)}`}>{p.occupancy_pct}%</div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-[12px] truncate">{p.location}</div>
              <div className="text-[10px] text-zinc-500 truncate">{p.state} · {p.capacity_beds} beds · ₹{p.adr || "—"} ADR</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
